import React from 'react';
import { Mail, Instagram, Globe, ShieldCheck, Download } from 'lucide-react';

// WhatsApp brand icon — matches the floating "Chat with sales" button
// for consistent visual signaling that wa.me links open WhatsApp, not a dialer
const WhatsAppIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

interface FooterProps {
  setView?: (view: string) => void;
}

/**
 * Footer type system — exactly three roles, applied uniformly:
 *   header : font-mono text-[10px] tracking-micro uppercase text-brass
 *   link   : text-sm text-paper/75 hover:text-paper (no icons except file/contact affordances)
 *   meta   : font-mono text-[10px] tracking-micro uppercase text-paper/60
 */
const Footer: React.FC<FooterProps> = ({ setView }) => {
  const go = (view: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    if (setView) setView(view);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const headerClass = 'font-mono text-[10px] tracking-micro uppercase text-brass mb-6';
  const linkClass = 'hover:text-paper transition-colors duration-150 text-left';
  const metaClass = 'font-mono text-[10px] tracking-micro uppercase text-paper/60';

  return (
    <footer className="relative bg-forge text-paper pt-24 lg:pt-32 pb-12 overflow-hidden">
      {/* Thin brass top rule */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brass/40 to-transparent" />

      <div className="relative max-w-editorial mx-auto px-6 lg:px-10">

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-x-10 gap-y-14 mb-20">

          {/* Brand */}
          <div className="lg:col-span-3">
            <img
              src="/images/logo-shield.webp"
              alt=""
              className="w-24 h-24 object-contain mb-5"
            />
            <p className="font-editorial italic text-2xl text-paper-warm leading-none tracking-tight">
              The Machete Man
            </p>
            <p className="font-mono text-[10px] tracking-micro uppercase text-brass mt-2.5">
              Blade sharp. Yard clean.
            </p>
            <p className={`${metaClass} mt-1.5`}>
              Proudly Canadian &middot; Toronto
            </p>

            <p className="text-paper/65 text-sm leading-[1.65] mt-6 mb-7 max-w-sm">
              Bringing the utility of the tropics to the Canadian backyard — authentic
              SAE&nbsp;1075 high-carbon steel tools, shipped direct to your door. Wholesale accounts
              welcome for shops and garden centres.
            </p>

            <div className="inline-flex items-start gap-2 text-ember-soft font-mono text-[10px] tracking-micro uppercase border border-paper/15 px-3 py-2.5 leading-relaxed">
              <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0 mt-px" strokeWidth={1.5} />
              <span>Canada&apos;s exclusive Incolma distributor</span>
            </div>

            <div className="mt-9 pt-7 border-t border-paper/10">
              <p className={`${metaClass} mb-3`}>— Official partner</p>
              <div className="bg-paper/5 border border-paper/10 p-3 inline-block">
                <img
                  src="/images/incolma-logo.webp"
                  alt="Incolma Colombia"
                  className="h-7 w-auto object-contain opacity-90"
                />
              </div>
            </div>
          </div>

          {/* Awareness */}
          <div className="lg:col-span-3">
            <p className={headerClass}>— Awareness</p>
            <ul className="space-y-3.5 text-sm text-paper/75">
              <li><button onClick={go('awareness')} className={linkClass}>Safety: velocity vs. mass</button></li>
              <li><button onClick={go('awareness')} className={linkClass}>Garden &amp; landscaping uses</button></li>
              <li><button onClick={go('awareness')} className={linkClass}>Camping &amp; bushcraft</button></li>
              <li><button onClick={go('awareness')} className={linkClass}>Rust prevention &amp; care</button></li>
            </ul>
          </div>

          {/* Shop & trade */}
          <div className="lg:col-span-3">
            <p className={headerClass}>— Shop &amp; trade</p>
            <ul className="space-y-3.5 text-sm text-paper/75">
              <li><button onClick={go('prototype')} className={linkClass}>Shop the collection</button></li>
              <li><button onClick={go('wholesale')} className={linkClass}>Wholesale pricing</button></li>
              <li><button onClick={go('catalog')} className={linkClass}>Product guide</button></li>
              <li><button onClick={go('warranty')} className={linkClass}>Warranty &amp; returns</button></li>
              <li>
                <a
                  href="/the-machete-man-catalog.pdf"
                  download
                  className={`${linkClass} inline-flex items-center gap-2`}
                >
                  <Download className="w-3.5 h-3.5 text-ember/80" strokeWidth={1.5} />
                  Catalog (PDF)
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <p className={headerClass}>— Contact</p>

            <ul className="space-y-3.5 text-sm text-paper/75">
              <li>
                <a
                  href="https://wa.me/14166840010?text=Hi!%20I'm%20interested%20in%20your%20machetes."
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${linkClass} inline-flex items-center gap-2.5 group`}
                >
                  <WhatsAppIcon className="w-4 h-4 fill-ember/80 group-hover:fill-ember transition-colors flex-shrink-0" />
                  <span>(416) 684-0010 <span className="text-paper/50">· Toronto</span></span>
                </a>
              </li>
              <li>
                <a href="mailto:info@themacheteman.com" className={`${linkClass} inline-flex items-center gap-2.5`}>
                  <Mail className="w-4 h-4 text-ember/80 flex-shrink-0" strokeWidth={1.5} />
                  info@themacheteman.com
                </a>
              </li>
              <li className="inline-flex items-center gap-2.5">
                <Globe className="w-4 h-4 text-ember/80 flex-shrink-0" strokeWidth={1.5} />
                themacheteman.com
              </li>
            </ul>

            <div role="separator" aria-hidden="true" className="my-6 border-t border-paper/10" />

            <div className="text-sm text-paper/65 leading-relaxed">
              <p>The Machete Man</p>
              <p>345 Nugget Avenue, Unit 17</p>
              <p>Scarborough, Ontario, Canada</p>
              <p className={`${metaClass} mt-2.5`}>Mon to Sat · 9am to 6pm ET</p>
            </div>

            {/* Facebook/YouTube icons removed 2026-09-22 — they were dead buttons.
                Re-add as real links once those accounts exist. */}
            <div className="flex gap-5 mt-7">
              <a
                href="https://instagram.com/the_macheteman"
                target="_blank"
                rel="noopener noreferrer"
                className="text-paper/55 hover:text-paper transition-colors duration-150 p-2 -m-2"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" strokeWidth={1.5} />
              </a>
            </div>
          </div>

        </div>

        {/* Bottom strip */}
        <div className="border-t border-paper/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-[11px] text-paper/60 font-mono">
          <p>&copy; 2026 Machetes and More Ltd. o/a The Machete Man &middot; Scarborough, ON &middot; All prices CAD</p>
          <div className="flex flex-wrap justify-center gap-6">
            <button onClick={go('legal')} className="hover:text-paper/85 transition-colors duration-150 py-2">Privacy</button>
            <button onClick={go('legal')} className="hover:text-paper/85 transition-colors duration-150 py-2">Terms of sale</button>
            <button onClick={go('legal')} className="hover:text-paper/85 transition-colors duration-150 py-2">Shipping &amp; returns</button>
            <button onClick={go('admin')} className="hover:text-paper/85 transition-colors duration-150 py-2">Portal</button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
